import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Navigate } from 'react-router-dom';
import { Gift, Ticket } from 'lucide-react';
import { toast } from 'sonner';

export default function Rewards() {
    const { type, loading, user, refresh } = useAuth();
    const [rewards, setRewards] = useState([]);
    const [filter, setFilter] = useState('all');
    const [myRedemptions, setMyRedemptions] = useState([]);

    useEffect(() => {
        api.get('/rewards').then(({ data }) => setRewards(data));
        if (type === 'customer') api.get('/rewards/redemptions/my').then(({ data }) => setMyRedemptions(data));
    }, [type]);

    const redeem = async (r) => {
        try {
            const { data } = await api.post(`/rewards/${r.id}/redeem`);
            toast.success(`Voucher issued: ${data.voucher_code}`);
            await refresh();
            const m = await api.get('/rewards/redemptions/my');
            setMyRedemptions(m.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not redeem');
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
    if (type !== 'customer') return <Navigate to="/login?next=/rewards" replace />;

    const cats = ['all', ...Array.from(new Set(rewards.map((r) => r.category)))];
    const filtered = filter === 'all' ? rewards : rewards.filter((r) => r.category === filter);

    return (
        <div className="pt-16 pb-32" data-testid="rewards-page">
            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 mb-12">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <div className="overline mb-4">Rewards Marketplace</div>
                        <h1 className="font-serif text-5xl md:text-6xl text-navy leading-tight">Spend points on <span className="italic text-gold">moments worth keeping.</span></h1>
                    </div>
                    <div className="card-luxury px-8 py-6">
                        <div className="overline">Your Balance</div>
                        <div className="font-serif text-4xl text-gold mt-2">{user?.points_balance?.toLocaleString() || 0}</div>
                        <div className="text-xs text-text-tertiary uppercase tracking-widest">points</div>
                    </div>
                </div>
            </section>

            <section className="border-y border-border-subtle bg-cream sticky top-20 z-20" data-testid="rewards-filter-bar">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-wrap gap-3">
                    {cats.map((c) => (
                        <button
                            key={c}
                            onClick={() => setFilter(c)}
                            data-testid={`rewards-cat-${c}`}
                            className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold transition-colors ${filter === c ? 'bg-navy text-white' : 'bg-white text-navy border border-border-subtle hover:border-gold'}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="rewards-grid">
                {filtered.map((r) => {
                    const canAfford = user && user.points_balance >= r.points_required;
                    const eligible = user && r.tier_eligibility.includes(user.tier);
                    return (
                        <div key={r.id} className="card-luxury overflow-hidden flex flex-col" data-testid={`reward-card-${r.id}`}>
                            <div className="aspect-[5/3] overflow-hidden">
                                <img src={r.image_url} alt="" className="w-full h-full object-cover" />
                            </div>
                            <div className="p-7 flex-1 flex flex-col">
                                <div className="overline text-gold mb-2">{r.brand_name}</div>
                                <h3 className="font-serif text-2xl text-navy leading-tight mb-2">{r.title}</h3>
                                <p className="text-sm text-text-secondary line-clamp-2 mb-4">{r.description}</p>
                                <div className="flex items-end justify-between mt-auto">
                                    <div>
                                        <div className="font-serif text-3xl text-gold">{r.points_required.toLocaleString()}</div>
                                        <div className="text-xs text-text-tertiary uppercase tracking-widest">points</div>
                                    </div>
                                    <button
                                        disabled={!canAfford || !eligible}
                                        onClick={() => redeem(r)}
                                        className={canAfford && eligible ? 'btn-primary' : 'btn-outline opacity-50'}
                                        data-testid={`redeem-btn-${r.id}`}
                                    >
                                        {!eligible ? `${r.tier_eligibility[r.tier_eligibility.length - 1]} only` : (canAfford ? 'Redeem' : 'Not enough')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </section>

            {myRedemptions.length > 0 && (
                <section className="max-w-7xl mx-auto px-6 md:px-12 mt-12" data-testid="my-vouchers">
                    <div className="overline mb-4">My Vouchers</div>
                    <h3 className="font-serif text-3xl text-navy mb-8">Issued vouchers</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {myRedemptions.map((v) => (
                            <div key={v.id} className="card-luxury p-7">
                                <Ticket size={24} className="text-gold mb-3" strokeWidth={1.3} />
                                <div className="font-serif text-lg text-navy">{v.reward_title}</div>
                                <div className="mt-4 bg-cream border border-dashed border-gold px-4 py-3 font-mono text-sm text-navy">{v.voucher_code}</div>
                                <div className="text-xs text-text-tertiary mt-3 uppercase tracking-widest">Valid till {new Date(v.valid_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
